import { useEffect, useMemo, useState } from 'react';
import { generateAuditPack } from '../lib/auditPack';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  getSite, listSiteDocuments, listForms, listAllPermits, listTickRuns,
  listSignInsBetween, listRams,
  type Site, type DocumentRow, type FormRow, type PermitRow,
  type TickListRun, type SignIn, type RamsItem,
} from '../lib/db';
import { cacheGet } from '../lib/cache';

// Activity feed item types. RAMS is included so a site's risk-assessment
// + method-statement documents show up in the same chronological view as
// everything else — the RAMS area remains the canonical home for the
// documents themselves (this is just a discovery / audit view).
type RecordType = 'tick' | 'form' | 'permit' | 'document' | 'rams' | 'signin_day';
type FilterKey = 'all' | RecordType;

interface RecordItem {
  id: string;
  type: RecordType;
  title: string;
  subtitle: string;
  isoDate: string;
  href: string;
  // Optional person attribution — used by the Person filter dropdown on
  // the Activity page so site managers can drill into "what did Aaron
  // log this week" without scanning every row. Not every record type
  // has a single person (e.g. a multi-person sign-in day), so this is
  // populated where it makes sense and left blank otherwise.
  person?: string;
}

const TYPE_LABELS: Record<RecordType, string> = {
  tick:        'Check',
  form:        'Form',
  permit:      'Permit',
  document:    'Document',
  rams:        'RAMS',
  signin_day:  'Sign-ins',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

export function SiteRecords() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();

  const [site, setSite] = useState<Site | null>(null);
  const [items, setItems] = useState<RecordItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState<string>(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 6); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [filter, setFilter] = useState<FilterKey>('all');
  const [personFilter, setPersonFilter] = useState<string>('');  // '' = all people
  const [packing, setPacking] = useState(false);
  const [packMsg, setPackMsg] = useState<string>('');
  // Persisted success result so the user sees an obvious confirmation
  // after the pack finishes. Tradesman-friendly: shows filename + total,
  // plus a manual re-download in case the browser blocked the auto one.
  const [packResult, setPackResult] = useState<{ filename: string; total: number; url: string } | null>(null);
  const [packError, setPackError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId || !org) return;
    let cancelled = false;

    // Cache-first paint of the site header / chrome. The records list
    // still waits on the network (7 parallel time-filtered queries) but
    // at least the page no longer flashes a loading state during the
    // navigation itself.
    cacheGet<Site>('sites', siteId).then((cached) => {
      if (cancelled) return;
      if (cached) setSite(cached);
    }).catch(() => {});

    setLoading(true); setError(null);
    (async () => {
      try {
        const fromIso = new Date(from + 'T00:00:00').toISOString();
        const toExclusiveIso = new Date(new Date(to + 'T23:59:59').getTime() + 1).toISOString();
        const [s, ticks, forms, permits, docs, signIns, ramsRows] = await Promise.all([
          getSite(siteId),
          listTickRuns(siteId).catch(() => []),
          listForms(siteId).catch(() => []),
          listAllPermits(siteId).catch(() => []),
          listSiteDocuments(siteId).catch(() => []),
          listSignInsBetween(siteId, fromIso, toExclusiveIso).catch(() => []),
          listRams(siteId).catch(() => []),
        ]);
        if (cancelled) return;
        setSite(s);

        const rows: RecordItem[] = [];

        for (const t of ticks as TickListRun[]) {
          if (t.performed_at && t.performed_at < fromIso) continue;
          if (t.performed_at && t.performed_at > toExclusiveIso) continue;
          rows.push({
            id: `tick-${t.id}`,
            type: 'tick',
            title: prettyTick(t.kind),
            subtitle: `Completed by ${t.performed_by_name ?? 'unknown'}`,
            isoDate: t.performed_at ?? '',
            href: `/sites/${siteId}/checks/${t.kind}/view`,
            person: t.performed_by_name ?? undefined,
          });
        }

        for (const f of forms as FormRow[]) {
          if (f.created_at < fromIso) continue;
          if (f.created_at > toExclusiveIso) continue;
          rows.push({
            id: `form-${f.id}`,
            type: 'form',
            title: prettyForm(f.kind),
            subtitle: f.title ?? '',
            isoDate: f.created_at,
            href: `/forms/${f.id}`,
          });
        }

        for (const p of permits as PermitRow[]) {
          if (p.created_at < fromIso) continue;
          if (p.created_at > toExclusiveIso) continue;
          rows.push({
            id: `permit-${p.id}`,
            type: 'permit',
            title: `Permit · ${p.template_id?.replace(/_/g, ' ') ?? 'unknown'}`,
            subtitle: p.status ?? '',
            isoDate: p.created_at,
            href: `/permits/${p.id}`,
          });
        }

        for (const d of docs as DocumentRow[]) {
          if (!d.is_current) continue;
          if (d.created_at < fromIso) continue;
          if (d.created_at > toExclusiveIso) continue;
          rows.push({
            id: `doc-${d.id}`,
            type: 'document',
            title: d.title,
            subtitle: d.template_id ? d.template_id.replace(/_/g, ' ') : 'document',
            isoDate: d.created_at,
            href: `/documents/${d.id}`,
          });
        }

        // RAMS — risk assessments + method statements for this site. Linked
        // through to /sites/:siteId/rams/list?share=<id> which opens the
        // share modal for the specific RAMS in the same list view the
        // RAMS area uses, so users can preview and email it on.
        for (const r of ramsRows as RamsItem[]) {
          if (r.created_at < fromIso) continue;
          if (r.created_at > toExclusiveIso) continue;
          const subtitleParts: string[] = [];
          if (r.company) subtitleParts.push(r.company);
          else if (r.trade) subtitleParts.push(r.trade);
          else if (r.trade_preset) subtitleParts.push(r.trade_preset);
          rows.push({
            id: `rams-${r.id}`,
            type: 'rams',
            title: r.title,
            subtitle: subtitleParts.join(' · ') || 'Risk assessment & method statement',
            isoDate: r.created_at,
            href: `/sites/${siteId}/rams/list?share=${r.id}`,
          });
        }

        // Group sign-ins by day so the Records list isn't drowned by individual rows.
        const byDay = new Map<string, SignIn[]>();
        for (const si of signIns as SignIn[]) {
          const k = dayKey(si.time_in);
          byDay.set(k, [...(byDay.get(k) ?? []), si]);
        }
        for (const [k, arr] of byDay.entries()) {
          rows.push({
            id: `signin-${k}`,
            type: 'signin_day',
            title: `Sign-ins — ${arr.length} ${arr.length === 1 ? 'person' : 'people'}`,
            subtitle: arr.map((s) => s.full_name).filter(Boolean).slice(0, 3).join(', ') + (arr.length > 3 ? `, +${arr.length - 3} more` : ''),
            isoDate: arr[0].time_in,
            href: `/sites/${siteId}/timesheet?day=${k}`,
          });
        }

        rows.sort((a, b) => (b.isoDate || '').localeCompare(a.isoDate || ''));
        setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, org, from, to]);

  const visible = useMemo(() => {
    let out = items;
    if (filter !== 'all') out = out.filter((it) => it.type === filter);
    if (personFilter)     out = out.filter((it) => it.person === personFilter);
    return out;
  }, [items, filter, personFilter]);

  // Unique person names across the visible record types, sorted A-Z, for
  // the Person dropdown. We compute from the unfiltered list so the user
  // can switch type chips without the person options disappearing.
  const uniquePeople = useMemo(() => {
    const names = new Set<string>();
    for (const it of items) {
      if (it.person && it.person.trim()) names.add(it.person.trim());
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const counts = useMemo(() => {
    const out: Record<FilterKey, number> = { all: items.length, tick: 0, form: 0, permit: 0, document: 0, rams: 0, signin_day: 0 };
    for (const it of items) out[it.type]++;
    return out;
  }, [items]);

  async function buildPack(): Promise<void> {
    if (!siteId || !org) return;
    // Clear any previous result/error before starting.
    setPackResult(null); setPackError(null);
    setPacking(true); setPackMsg('Starting…');
    try {
      const fromIso = new Date(from + 'T00:00:00').toISOString();
      const toExclusiveIso = new Date(new Date(to + 'T23:59:59').getTime() + 1).toISOString();
      const { blob, filename, counts } = await generateAuditPack({
        orgName: org.org_name,
        siteId,
        fromIso,
        toIso: toExclusiveIso,
        onProgress: (msg, done, total) => setPackMsg(`${msg} (${done}/${total})`),
      });
      // Keep the object URL alive so the user can re-trigger the download
      // from the success card if their browser blocked the first one.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      const total = Object.values(counts).reduce((acc, v) => acc + v, 0);
      setPackResult({ filename, total, url });
      setPackMsg('');
    } catch (e) {
      setPackError(e instanceof Error ? e.message : String(e));
      setPackMsg('');
    } finally {
      setPacking(false);
    }
  }

  if (loading && !site) return <AppShell title="Activity" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (error)            return <AppShell title="Activity" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error}</div></AppShell>;
  if (!site)            return <AppShell title="Activity" back="/"><div className="banner banner-error">Site not found.</div></AppShell>;

  return (
    <AppShell title="Activity" back={`/sites/${siteId}`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{site.name}</p>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Past tick lists, forms, permits, documents and sign-ins. Use this on an HSE visit or for an audit.
        </p>
      </div>

      <div className="rec-filter-row">
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="rec-from" style={{ fontSize: 12 }}>From</label>
          <input id="rec-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="rec-to" style={{ fontSize: 12 }}>To</label>
          <input id="rec-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {uniquePeople.length > 0 && (
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="rec-person" style={{ fontSize: 12 }}>Person</label>
            <select
              id="rec-person"
              value={personFilter}
              onChange={(e) => setPersonFilter(e.target.value)}
            >
              <option value="">All people</option>
              {uniquePeople.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <button type="button"
        className="btn btn-primary btn-hse-pack"
        style={{ marginBottom: 12 }}
        onClick={buildPack}
        disabled={packing}>
        {packing ? `📦 Packing… ${packMsg}` : '📦 Pack everything for HSE visit'}
      </button>
      {!packing && packResult && (
        <div
          role="status"
          style={{
            margin: '0 0 12px',
            padding: '12px 14px',
            background: 'rgba(22, 163, 74, 0.10)',
            border: '1px solid var(--success, #16A34A)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>✅</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, color: 'var(--success, #16A34A)', fontSize: 14 }}>
              Pack saved — check Downloads
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
              {packResult.total} records packed into <strong style={{ wordBreak: 'break-all' }}>{packResult.filename}</strong>
            </p>
            <a
              href={packResult.url}
              download={packResult.filename}
              style={{ display: 'inline-block', marginTop: 6, fontSize: 13, fontWeight: 600, color: 'var(--brand-deep)' }}
            >
              Download again →
            </a>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => { URL.revokeObjectURL(packResult.url); setPackResult(null); }}
            style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--ink-soft)', padding: 0, lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}
      {!packing && packError && (
        <div
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '12px 14px',
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid var(--danger)',
            borderRadius: 10,
          }}
        >
          <p style={{ margin: 0, fontWeight: 700, color: 'var(--danger)', fontSize: 14 }}>Couldn't build the pack</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>{packError}</p>
        </div>
      )}

      <div className="rec-chip-row">
        {(['all','tick','form','permit','document','rams','signin_day'] as FilterKey[]).map((k) => (
          <button key={k} type="button"
            className={`rec-chip${filter === k ? ' active' : ''}`}
            onClick={() => setFilter(k)}>
            {k === 'all' ? 'All' : TYPE_LABELS[k as RecordType]} ({counts[k]})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="card" style={{ marginTop: 12, textAlign: 'center' }}>
          <p className="muted" style={{ margin: 0 }}>Nothing in this range. Widen the dates or change the filter.</p>
        </div>
      ) : (
        <ul className="rec-list">
          {visible.map((it) => (
            <li key={it.id}>
              <Link to={it.href} className="rec-row">
                <div>
                  <p className="rec-title">{it.title}</p>
                  {it.subtitle && <p className="rec-sub">{it.subtitle}</p>}
                  <p className="rec-date">{it.isoDate ? fmtDate(it.isoDate) : '—'}</p>
                </div>
                <span className={`rec-pill rec-pill-${it.type}`}>{TYPE_LABELS[it.type]}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}

function prettyTick(kind: string): string {
  if (kind === 'daily') return 'Daily check';
  if (kind === 'weekly') return 'Weekly site walk';
  if (kind === 'monthly') return 'Monthly review';
  if (kind === 'telehandler_weekly') return 'Telehandler weekly check';
  return kind;
}

function prettyForm(kind: string): string {
  if (kind === 'toolbox_talk') return 'Toolbox talk';
  if (kind === 'accident') return 'Accident / near-miss';
  if (kind === 'subcontractor_induction') return 'Subbie induction';
  return kind;
}
